/**
 * TaskTimeCard — "Zeiterfassung" in the task workspace sidebar
 * (TASK-UX1).
 *
 * The ONE place the task's booked effort is stated. The reports section
 * in the main column lists the individual rows and, on this page, hides
 * its own KPI row (``showSummary={false}``) so the total is not printed
 * twice on the same screen.
 *
 * The numbers come from the server-side aggregate over EVERY linked
 * report, never from the ten rows the list happens to have loaded — the
 * card and the list read the same hook, so they cannot disagree.
 *
 * A failed summary shows its own error and retry here rather than
 * blanking the card: the rows below stay perfectly usable without it.
 *
 * No "Rapport erfassen" button: the page header already carries it, and
 * the reports section offers it again in its empty state. A third copy
 * would be a third accessible name saying the same thing.
 */
import React from "react";
import { Clock } from "lucide-react";

import {
  formatMinutes,
  formatWorkDate,
} from "../calendar/workReports/workReportConstants";
import {
  reportCountLabel,
  spokenDuration,
} from "./entityWorkReports/EntityWorkReportsSection";

function Stat({ label, value, valueLabel }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border/60 py-1.5 last:border-b-0 dark:border-[#1e2130]/60">
      <span className="shrink-0 text-[11px] font-medium uppercase tracking-[0.05em] text-muted-foreground dark:text-[#4a5070]">
        {label}
      </span>
      <span
        className="min-w-0 text-right text-[13px] font-semibold tabular-nums text-foreground dark:text-[#c5cbe0]"
        aria-label={valueLabel}
      >
        {value}
      </span>
    </div>
  );
}

export default function TaskTimeCard({ workReports, headingId }) {
  const { summary, summaryError, retrySummary } = workReports;

  return (
    <section
      aria-labelledby={headingId}
      className="rounded-[10px] border border-border bg-card p-4 dark:border-[#1e2130] dark:bg-[#13161f]"
    >
      <header className="mb-3 flex items-center justify-between gap-3">
        <h2
          id={headingId}
          className="m-0 flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.09em] text-muted-foreground dark:text-[#4a5070]"
        >
          <Clock className="h-3.5 w-3.5" aria-hidden="true" />
          Zeiterfassung
        </h2>
      </header>

      <div className="grid gap-0">
        {/* REPORT-PEOPLE: "Personenzeit", not "Gesamtaufwand". The
            number is the same SUM(duration_minutes) it always was, but
            once three people can book the same hour it is 180 minutes
            of working time, not a three-hour job — and "Gesamtaufwand"
            reads like the latter. */}
        <Stat
          label="Personenzeit"
          value={summary ? formatMinutes(summary.total_minutes ?? 0) : "–"}
          valueLabel={
            summary
              ? `Personenzeit: ${spokenDuration(summary.total_minutes ?? 0)}`
              : undefined
          }
        />
        <Stat
          label="Personenbuchungen"
          value={summary ? reportCountLabel(summary.report_count) : "–"}
        />
        <Stat
          label="Letzter Rapport"
          value={
            summary
              ? summary.last_report_date
                ? formatWorkDate(summary.last_report_date)
                : "Noch kein Rapport"
              : "–"
          }
        />
      </div>

      {summaryError ? (
        <div
          role="alert"
          className="mt-3 flex flex-wrap items-center gap-2 rounded-[8px] border border-destructive/40 bg-destructive/10 px-3 py-2 text-[11px] text-destructive"
        >
          <span>{summaryError}</span>
          <button
            type="button"
            onClick={retrySummary}
            className="rounded-[6px] border border-destructive/40 bg-transparent px-2 py-0.5 text-[11px] font-semibold hover:bg-destructive/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Erneut versuchen
          </button>
        </div>
      ) : null}
    </section>
  );
}
